import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthUser } from '../models/auth-user.model';
import { StorageService } from './storage.service';
import { AmplitudeService } from './amplitude.service';
import { IamService } from './iam.service';
import { setSentryUser, clearSentryUser } from '../services/sentry.util';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly storage = inject(StorageService);
  private readonly amplitude = inject(AmplitudeService);
  private readonly iam = inject(IamService);

  private readonly _currentUser = signal<AuthUser | null>(this.storage.getCurrentUser());

  readonly currentUser = this._currentUser.asReadonly();
  readonly isLogged = computed(() => this._currentUser() !== null);

  getToken(): string | null {
    return this._currentUser()?.token ?? null;
  }

  setCurrentUser(user: AuthUser): void {
    this._currentUser.set(user);
    this.storage.setCurrentUser(user);
    setSentryUser(user);
    this.amplitude.init(user);
  }

  logout(): void {
    clearSentryUser();
    this.amplitude.destroy();
    this._currentUser.set(null);
    this.storage.setCurrentUser(null);
  }

  async refreshUser(): Promise<void> {
    const user = this._currentUser();
    if (!user) return;

    try {
      const updatedUser = await firstValueFrom(this.iam.me());
      this.setCurrentUser({ ...updatedUser, token: user.token });
    } catch {
      this.logout();
    }
  }
}
